// components/Layout/Topbar/NotificationDropdown.jsx
import React, { useRef, useEffect, useState } from "react";
import { IoIosNotifications } from "react-icons/io";
import { MdCheck, MdCheckCircle, MdInfo, MdWarning, MdError } from "react-icons/md";
import axios from "axios";
import { api_url } from "../../utils/data";
import { toast } from "react-toastify";

const NotificationDropdown = ({ 
  notifications, 
  authToken, 
  isOpen, 
  setIsOpen, 
  closeOthers,
  getNotifications,
}) => {
  const dropdownRef = useRef(null);
  const [expandedNotifications, setExpandedNotifications] = useState(new Set());

  const getNotificationIcon = (type) => {
    const iconProps = { className: "w-5 h-5" };
    switch (type) {
      case 'success': return <MdCheckCircle {...iconProps} className="w-5 h-5 text-green-500" />;
      case 'info': return <MdInfo {...iconProps} className="w-5 h-5 text-blue-500" />;
      case 'warning': return <MdWarning {...iconProps} className="w-5 h-5 text-yellow-500" />;
      case 'low_stock': return <MdWarning {...iconProps} className="w-5 h-5 text-yellow-500" />;
      case 'expiry':return <MdWarning {...iconProps} className="w-5 h-5 text-yellow-500" />;
      case 'error': return <MdError {...iconProps} className="w-5 h-5 text-red-500" />;
      default: return <MdInfo {...iconProps} className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTimeAgo = (notification) => {
    if (!notification.created_at) {
      return 'Just now';
    }
  
    const now = new Date();
    const createdAt = new Date(notification.created_at);
    const diffInSeconds = Math.floor((now - createdAt) / 1000);
  
    if (diffInSeconds < 60) {
      return diffInSeconds <= 1 ? 'Just now' : `${diffInSeconds} secs ago`;
    }
  
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return diffInMinutes === 1 ? '1 min ago' : `${diffInMinutes} mins ago`;
    }
  
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
    }
  
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
    }
  
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
      return diffInWeeks === 1 ? '1 week ago' : `${diffInWeeks} weeks ago`;
    }
  
    // For anything older than a month, show the actual date
    return createdAt.toLocaleDateString();
  };

  const getNotificationBg = (type, isRead) => {
    if (isRead) return 'bg-white';
    switch (type) {
      case 'success': return 'bg-green-50 border-l-4 border-green-400';
      case 'info': return 'bg-blue-50 border-l-4 border-blue-400';
      case 'warning': return 'bg-yellow-50 border-l-4 border-yellow-400';
      case 'error': return 'bg-red-50 border-l-4 border-red-400';
      default: return 'bg-gray-50 border-l-4 border-gray-400';
    }
  };

  // Function to truncate text to specified number of words
  const truncateText = (text, wordLimit = 7) => {
    if (!text) return '';
    const words = text.split(' ');
    return words.length > wordLimit ? words.slice(0, wordLimit).join(' ') : text;
  };

  // Function to check if text needs truncation
  const needsTruncation = (text, wordLimit = 7) => {
    if (!text) return false;
    return text.split(' ').length > wordLimit;
  };

  // Function to toggle expanded state for a notification
  const toggleExpanded = (notificationId, e) => {
    e.stopPropagation(); // Prevent marking as read when clicking read more/less
    setExpandedNotifications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  const markAsRead = async (notification_id) => {
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + authToken,
      },
    };
    const body = JSON.stringify({ is_read: true });
    
    try {
      await axios.put(
        `${api_url}/accounts/notifications/${notification_id}/`,
        body,
        config
      );
      getNotifications();
      toast.success("Marked as read");
    } catch (err) {
      console.error(err);
    }
  };

  const markAllAsRead = async () => {
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + authToken,
      },
    };
    const body = JSON.stringify({ is_read: true });
    
    try {
      await axios.put(
        `${api_url}/accounts/notifications/`,
        body,
        config
      );
      getNotifications();
      toast.success("Marked as read");
    } catch (err) {
      console.error(err);
    }
  };

  const toggleDropdown = () => {
    closeOthers();
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <IoIosNotifications className="w-8 h-8" />
        {notifications.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-medium">
            {notifications.length}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white shadow-lg border border-gray-200 z-50 max-h-96 overflow-y-auto">
          <div className="py-2">
            <div className="flex items-center justify-between">
              <div className="px-4 py-2 text-[13px] text-gray-700 border-b border-gray-100">
                Notifications
              </div>
              <div onClick={()=>markAllAsRead()} className="cursor-pointer hover:text-blue-600 text-blue-500 px-4 py-2 text-[12px] text-gray-700 border-b border-gray-100">
                Mark all as read
              </div>
            </div>
            {notifications.length === 0 ? (
              <div className="px-4 py-6 text-center text-gray-500 text-sm">
                No notifications
              </div>
            ) : (
              notifications.map((notification, index) => {
                const isExpanded = expandedNotifications.has(notification.id);
                const showTruncated = needsTruncation(notification.details);
                
                return(
                  <div
                  key={notification.id || index}
                  className={`relative px-2 py-3 hover:bg-gray-50 cursor-pointer transition-all duration-150 group ${
                    getNotificationBg(notification?.notification_type, notification.is_read)
                  }`}
                  onClick={() => markAsRead(notification.id)}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification?.notification_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <h4 className={`text-sm font-semibold mb-0 ${
                          notification.is_read ? 'text-gray-700' : 'text-gray-900'
                        }`}>
                          {notification.title || 'Notification'}
                        </h4>
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {getTimeAgo(notification)}
                        </span>
                      </div>
                      <p className={`text-[13px] mb-0 ${
                        notification.is_read ? 'text-gray-600' : 'text-gray-800'
                      }`}>
                        {isExpanded || !showTruncated 
                          ? notification.details 
                          : truncateText(notification.details)
                        }
                        {showTruncated && (
                          <span
                            onClick={(e) => toggleExpanded(notification.id, e)}
                            className="text-blue-500 hover:text-blue-700 cursor-pointer ml-1 text-xs font-medium"
                          >
                            {isExpanded ? ' Show less' : '... Read more'}
                          </span>
                        )}
                      </p>
                    </div>
                    {!notification.is_read && (
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      </div>
                    )}
                  </div>
                  
                  {/* Hover effect overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none"></div>
                </div>
                )
              }
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;